/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type ReactNode,
	type RefAttributes,
} from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	content: {
		borderRadius: token('radius.large'),
	},
	padding: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	margin: {
		marginBlock: token('space.150'),
		marginInline: '0',
	},
	accent: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
	},
	accentV2: {
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('color.background.accent.blue.bolder'),
	},
	accentV2Hovered: {
		backgroundColor: token('color.background.accent.blue.bolder.hovered'),
	},
});

const pinchCornerStyles = cssMap({
	topLeft: {
		borderTopLeftRadius: token('radius.xsmall'),
	},
	topRight: {
		borderTopRightRadius: token('radius.xsmall'),
	},
	bottomLeft: {
		borderBottomLeftRadius: token('radius.xsmall'),
	},
	bottomRight: {
		borderBottomRightRadius: token('radius.xsmall'),
	},
});

const pinchCornerV2Styles = cssMap({
	topLeft: {
		borderTopLeftRadius: token('radius.small'),
	},
	topRight: {
		borderTopRightRadius: token('radius.small'),
	},
	bottomLeft: {
		borderBottomLeftRadius: token('radius.small'),
	},
	bottomRight: {
		borderBottomRightRadius: token('radius.small'),
	},
});

type PinchCorner = 'none' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export interface PaperProps {
	children: ReactNode;
	verticalSpace?: boolean;
	style?: React.CSSProperties;
	pinchCorner?: PinchCorner;
	variant?: 'accent' | 'accentV2' | 'neutral' | 'default';
	testId?: string;
	showPadding?: boolean;
	mode?: string;
	isHovered?: boolean;
}

export const Paper: ForwardRefExoticComponent<PaperProps & RefAttributes<HTMLObjectElement>> =
	forwardRef<HTMLObjectElement, PaperProps>(
		(
			{
				children,
				verticalSpace = true,
				pinchCorner = 'none',
				style,
				variant,
				testId,
				showPadding = true,
				mode,
				isHovered = false,
			},
			ref,
		) => {
			const getBackgroundColor = () => {
				if (mode?.toLowerCase() === 'mini-modal') {
					return 'elevation.surface.raised';
				}

				if (variant === 'accent') {
					return 'color.background.accent.blue.subtlest';
				}

				if (variant === 'neutral') {
					return 'elevation.surface';
				}

				return 'elevation.surface';
			};

			return (
				<Box
					ref={ref}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={style}
					backgroundColor={
						variant === 'accentV2' || variant === 'accent' ? undefined : getBackgroundColor()
					}
					xcss={cx(
						styles.content,
						showPadding && styles.padding,
						verticalSpace && styles.margin,
						variant === 'accent' && styles.accent,
						variant === 'accentV2' && styles.accentV2,
						isHovered && variant === 'accentV2' && styles.accentV2Hovered,
						pinchCorner && pinchCorner !== 'none'
							? variant === 'accentV2'
								? pinchCornerV2Styles[pinchCorner]
								: pinchCornerStyles[pinchCorner]
							: false,
					)}
					testId={testId}
				>
					{children}
				</Box>
			);
		},
	);

type PaperErrorMessageProps = {
	message: React.ReactNode;
	contentUnder?: React.ReactNode;
	hideIcon?: boolean;
} & Omit<PaperProps, 'children'>;

export const PaperErrorMessage = ({
	message,
	contentUnder,
	hideIcon = false,
	...restProps
}: PaperErrorMessageProps): JSX.Element => {
	return (
		<Paper {...restProps}>
			<Stack space="space.100">
				<Inline alignBlock="center" space="space.100">
					{!hideIcon && <StatusErrorIcon label="" color="var(--ds-icon-danger)" />}
					<Text>{message}</Text>
				</Inline>
				{contentUnder}
			</Stack>
		</Paper>
	);
};
